import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigationManager } from '../../../core';
import { RevoltCheckbox, StepProgress } from '../../../components';
import {
  legalDocuments,
  buildRegistrationLegalAcceptance,
  areAllLegalDocumentsChecked,
  formatLegalPolicyVersion,
} from '../../account/legalDocuments';
import { RegisterLegalDocumentModal } from './RegisterLegalDocumentModal';
import '../../../components/LegalAcceptanceRequiredModal.css';
import './RegisterForm.css';

const REGISTER_STEPS = [
  { label: 'Account' },
  { label: 'Password' },
  { label: 'Policies' },
];

const STEP_SUBTITLES = [
  'Enter your email and username',
  'Choose a secure password',
  'Review and accept our policies',
];

function RegisterForm({ onSwitchToLogin }) {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const navigationManager = useNavigationManager();
  const [step, setStep] = useState(0);
  const [validatedSteps, setValidatedSteps] = useState([0]);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [legalChecked, setLegalChecked] = useState(() =>
    Object.fromEntries(legalDocuments.map((d) => [d.acceptanceKey, false]))
  );
  const [viewLegalDoc, setViewLegalDoc] = useState(null);

  useEffect(() => {
    if (success && isAuthenticated) {
      const redirectCompleted = navigationManager.completeAuthRedirect();
      if (!redirectCompleted) {
        navigate('/main');
      }
    }
  }, [success, isAuthenticated, navigationManager, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const validateAccountStep = () => {
    const email = formData.email.trim();
    const username = formData.username.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address');
      return false;
    }

    if (username.length < 3 || username.length > 30) {
      setError('Username must be 3-30 characters');
      return false;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setError('Username can only contain letters, numbers, hyphens, and underscores');
      return false;
    }

    return true;
  };

  const validatePasswordStep = () => {
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const validatePoliciesStep = () => {
    if (!areAllLegalDocumentsChecked(legalChecked)) {
      setError('You must accept all policies to create an account');
      return false;
    }
    return true;
  };

  const goToStep = (nextStep) => {
    setStep(nextStep);
    setError('');
  };

  const handleNext = () => {
    if (step === 0) {
      if (!validateAccountStep()) return;
      setValidatedSteps([0, 1]);
      goToStep(1);
      return;
    }
    if (step === 1) {
      if (!validatePasswordStep()) return;
      setValidatedSteps([0, 1, 2]);
      goToStep(2);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      goToStep(step - 1);
    }
  };

  const handleStepClick = (index) => {
    if (!validatedSteps.includes(index) || index === step) return;
    goToStep(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step < 2) {
      handleNext();
      return;
    }

    if (!validateAccountStep() || !validatePasswordStep() || !validatePoliciesStep()) {
      return;
    }

    setLoading(true);
    setError('');

    const result = await register(
      formData.email.trim(),
      formData.username.trim(),
      formData.password,
      buildRegistrationLegalAcceptance(legalChecked)
    );

    if (!result.success) {
      if (result.errors?.length > 0) {
        setError(result.errors.map((err) => err.msg || err.message).join(', '));
      } else if (result.error === 'legal_acceptance_required') {
        setError('You must accept all policies to create an account');
        goToStep(2);
      } else {
        setError(result.error || 'Registration failed');
      }
      setLoading(false);
      return;
    }

    setSuccess(true);
  };

  if (success) {
    return (
      <div className="auth-form register-form">
        <div className="auth-header">
          <h1 className="auth-title">Account Created!</h1>
          <p className="auth-subtitle">Welcome to Revolt</p>
        </div>

        <div className="alert alert-success">
          <strong>Registration successful!</strong>
          <br />
          Redirecting you to the app...
        </div>
      </div>
    );
  }

  const policiesComplete = areAllLegalDocumentsChecked(legalChecked);
  const isLastStep = step === 2;

  return (
    <form className="auth-form register-form" onSubmit={handleSubmit}>
      <div className="auth-header">
        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">{STEP_SUBTITLES[step]}</p>
      </div>

      <StepProgress
        className="register-step-progress"
        steps={REGISTER_STEPS}
        currentStep={step}
        validatedSteps={validatedSteps}
        onStepClick={handleStepClick}
      />

      {error && <div className="alert alert-error">{error}</div>}

      {step === 0 && (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-input"
              placeholder="username"
              value={formData.username}
              onChange={handleChange}
              autoComplete="username"
              required
              minLength={3}
              maxLength={30}
            />
            <small style={{ color: '#666', fontSize: '0.8rem' }}>
              3-30 characters, letters, numbers, hyphens, and underscores only
            </small>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="form-input"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              required
            />
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <p className="register-form__policy-intro">
            Accept each policy to finish creating your account. Click a policy name to read it.
          </p>
          <ul className="register-form__policy-list legal-acceptance-modal__list">
            {legalDocuments.map((doc) => (
              <li key={doc.slug} className="legal-acceptance-modal__row">
                <button
                  type="button"
                  className="legal-acceptance-modal__card"
                  onClick={() => setViewLegalDoc(doc)}
                  disabled={loading}
                >
                  <span className="legal-acceptance-modal__card-body">
                    <span className="legal-acceptance-modal__doc-name">
                      {doc.displayName}
                    </span>
                    <span className="legal-acceptance-modal__version">
                      Effective {formatLegalPolicyVersion(doc.requiredVersion)}
                    </span>
                  </span>
                </button>
                <RevoltCheckbox
                  className="legal-acceptance-modal__check"
                  id={`register-legal-${doc.slug}`}
                  ariaLabel={`I accept the ${doc.displayName}`}
                  checked={legalChecked[doc.acceptanceKey] === true}
                  onChange={(value) =>
                    setLegalChecked((prev) => ({
                      ...prev,
                      [doc.acceptanceKey]: value,
                    }))
                  }
                  disabled={loading}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      <div
        className={`register-form__actions${
          step === 2 ? ' register-form__actions--policies-step' : ''
        }`}
      >
        <div
          className={`register-form__actions-row${
            step === 0 ? ' register-form__actions-row--single' : ''
          }`}
        >
          {step > 0 && (
            <button
              type="button"
              className="register-form__back-button"
              onClick={handleBack}
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className="form-button"
            disabled={loading || (isLastStep && !policiesComplete)}
          >
            {loading
              ? 'Creating Account...'
              : isLastStep
                ? 'Create Account'
                : 'Continue'}
          </button>
        </div>
      </div>

      <RegisterLegalDocumentModal
        document={viewLegalDoc}
        onClose={() => setViewLegalDoc(null)}
      />

      <div className="form-footer">
        Already have an account?{' '}
        <button type="button" className="form-link" onClick={onSwitchToLogin}>
          Sign in
        </button>
      </div>

      <style>{`
        .auth-form {
          width: 100%;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 20px;
        }

        .auth-title {
          font-size: 1.8rem;
          font-weight: 300;
          margin-bottom: 8px;
          background: linear-gradient(135deg, #ffffff 0%, #ff4444 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .auth-subtitle {
          font-size: 0.95rem;
          color: #999;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-size: 0.9rem;
          color: #ccc;
          font-weight: 500;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(30, 30, 30, 0.6);
          border: 1px solid rgba(139, 0, 0, 0.3);
          border-radius: 8px;
          color: #fff;
          font-size: 0.95rem;
        }

        .form-input:focus {
          outline: none;
          border-color: #8b0000;
        }

        .form-input:-webkit-autofill,
        .form-input:-webkit-autofill:hover,
        .form-input:-webkit-autofill:focus,
        .form-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px rgba(30, 30, 30, 0.6) inset !important;
          -webkit-text-fill-color: #fff !important;
          background: rgba(30, 30, 30, 0.6) !important;
          border: 1px solid rgba(139, 0, 0, 0.3) !important;
          border-radius: 8px !important;
          transition: background-color 5000s ease-in-out 0s !important;
        }

        .form-input:valid,
        .form-input:not(:placeholder-shown) {
          background: rgba(30, 30, 30, 0.6) !important;
          color: #fff !important;
          border-color: rgba(139, 0, 0, 0.3) !important;
        }

        .form-button {
          padding: 14px;
          background: linear-gradient(135deg, #8b0000, #ff4444);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
        }

        .form-button:hover:not(:disabled) {
          transform: translateY(-2px);
        }

        .form-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .form-footer {
          text-align: center;
          font-size: 0.9rem;
          color: #999;
        }

        .form-link {
          color: #ff4444;
          background: none;
          border: none;
          cursor: pointer;
          text-decoration: underline;
          font-size: inherit;
        }

        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 0.9rem;
        }

        .alert-success {
          background: rgba(40, 167, 69, 0.1);
          border: 1px solid rgba(40, 167, 69, 0.3);
          color: #4ade80;
        }

        .alert-error {
          background: rgba(220, 53, 69, 0.1);
          border: 1px solid rgba(220, 53, 69, 0.3);
          color: #f87171;
        }
      `}</style>
    </form>
  );
}

export default RegisterForm;
